import { useEffect, useState } from 'react'
import type { Message, MessageOption } from '../../api/types'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { Icon } from '../../components/Icon'
import { formatClock } from '../../lib/format'
import { endedCaption, questionStatus } from './questions'
import { useExpired } from './useExpired'

interface Props {
  /** A bot's question: a message with options. */
  question: Message
  /** The id of the question's words, which name the group of answers. */
  labelId: string
  /** The option this device answered with, sent or still sending. */
  localAnswer: string | null
  /** The conversation's bot has a webhook, so it hears answers. */
  botAccepts: boolean
  onAnswer: (option: MessageOption) => void
  /** Who asked, for "Closed by Rota". */
  botName?: string | null
}

/**
 * A bot's question's answers, one per row. Tapping one sends it as your reply; a danger answer asks first. The buttons lock at the
 * first tap, before the answer is even queued, so a quick second tap sends nothing.
 */
export function MessageOptions({ question, labelId, localAnswer, botAccepts, onAnswer, botName }: Props) {
  const [tapped, setTapped] = useState<string | null>(null)
  const [confirming, setConfirming] = useState<MessageOption | null>(null)
  const expireAt = question.optionsExpireAt ?? null
  const expired = useExpired(expireAt)
  // The tap only bridges the moment before the outbox has the answer. Once the outbox or the server speaks for it, let go: an answer
  // the server then refuses leaves the outbox, and the question shows as it really is rather than ticked.
  useEffect(() => {
    if (localAnswer || question.answered) setTapped(null)
  }, [localAnswer, question.answered])
  const chosen = question.answered?.optionId ?? localAnswer ?? tapped
  const status = questionStatus({ chosen, closedAt: question.optionsClosedAt ?? null, expired, botAccepts })
  const captionId = `${labelId}-caption`
  const caption =
    status === 'closed'
      ? endedCaption({ closedAt: question.optionsClosedAt ?? null, expireAt, expired, botName: botName ?? null })
      : status === 'open' && expireAt
        ? `Answer by ${formatClock(expireAt)}`
        : null

  const send = (option: MessageOption) => {
    setTapped(option.id)
    onAnswer(option)
  }

  return (
    // A hold or a swipe that starts on an answer is a press, as in the reactions row: no menu, no swipe to reply.
    <div className="message-options" data-no-gestures>
      <div role="group" aria-labelledby={labelId} aria-describedby={caption ? captionId : undefined} className="message-options-list">
        {(question.options ?? []).map((option) => {
          const picked = chosen === option.id
          return (
            <button
              key={option.id}
              type="button"
              className={['message-option', option.style, picked ? 'is-chosen' : ''].filter(Boolean).join(' ')}
              aria-pressed={picked}
              disabled={status !== 'open'}
              onClick={() => (option.style === 'danger' ? setConfirming(option) : send(option))}
            >
              {picked ? <Icon name="check" /> : null}
              <span>{option.label}</span>
            </button>
          )
        })}
      </div>
      {caption ? (
        <p id={captionId} className="message-options-caption">
          {caption}
        </p>
      ) : null}
      {confirming ? (
        <ConfirmDialog
          open
          title={`Send '${confirming.label}'?`}
          confirmLabel="Send"
          danger
          onClose={() => setConfirming(null)}
          onConfirm={() => send(confirming)}
        >
          <p>It goes to the bot straight away and can't be changed.</p>
        </ConfirmDialog>
      ) : null}
    </div>
  )
}
